/**
 * AudioPipeline - 前端音频管线
 *
 * 集成 RNNoise WASM 降噪 + Silero VAD 语音检测。
 *
 * 管线架构:
 *   Mic (48kHz) → AudioContext → RNNoise AudioWorklet (降噪)
 *                                     ↓
 *                              MediaStreamDestination
 *                                 /           \
 *                          MediaRecorder    Silero VAD
 *                          (WebM/Opus)     (语音检测)
 *                               |               |
 *                               └── gate ────────┘
 *                                     |
 *                              WebSocket send
 *                          (仅当 isSpeaking=true)
 */

// 通过 script 标签注入加载 vad-web（绕过 CRA Webpack 对 onnxruntime-web 的兼容性问题）
let MicVADClass = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    // 检查是否已加载
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

async function loadVadModule() {
  if (MicVADClass) return MicVADClass;
  try {
    // 1. 加载 ONNX Runtime（暴露 window.ort）
    await loadScript('/vad/ort.min.js');
    // 配置 ONNX WASM 路径
    if (window.ort) {
      window.ort.env.wasm.wasmPaths = '/vad/';
    }
    // 2. 加载 vad-web bundle（依赖 window.ort，暴露 window.vad）
    await loadScript('/vad/bundle.min.js');
    if (window.vad && window.vad.MicVAD) {
      MicVADClass = window.vad.MicVAD;
      console.log('[AudioPipeline] vad-web loaded via script injection');
      return MicVADClass;
    }
    throw new Error('window.vad.MicVAD not found after loading bundle');
  } catch (e) {
    console.warn('[AudioPipeline] Failed to load vad-web:', e);
    return null;
  }
}

/**
 * @typedef {Object} PipelineOptions
 * @property {function(Blob): void} onAudioData - 收到 WebM/Opus 音频块时的回调
 * @property {function(boolean): void} [onVadChange] - VAD 状态变化回调
 * @property {function(string): void} [onError] - 错误回调
 * @property {function(): void} [onReady] - 管线就绪回调
 * @property {boolean} [enableDenoise=true] - 是否启用降噪
 * @property {boolean} [enableVad=true] - 是否启用 VAD 门控
 * @property {number} [vadThreshold=0.5] - VAD 阈值 (0~1)
 * @property {number} [speechPadMs=300] - 语音前后的填充毫秒数
 */

export default class AudioPipeline {
  /**
   * @param {PipelineOptions} options
   */
  constructor(options) {
    this.onAudioData = options.onAudioData;
    this.onVadChange = options.onVadChange || (() => {});
    this.onError = options.onError || ((e) => console.error('[AudioPipeline]', e));
    this.onReady = options.onReady || (() => {});
    this.enableDenoise = options.enableDenoise !== false;
    this.enableVad = options.enableVad !== false;
    this.vadThreshold = options.vadThreshold || 0.5;
    this.speechPadMs = options.speechPadMs || 300;

    // 状态
    this.isSpeaking = false;
    this.isRunning = false;

    // 内部引用
    this._audioContext = null;
    this._sourceNode = null;
    this._denoiseNode = null;
    this._destinationNode = null;
    this._mediaRecorder = null;
    this._fullRecorder = null;
    this._micVad = null;
    this._micStream = null;

    // 完整录音数据
    this._fullAudioChunks = [];

    // VAD 门控：音频块缓冲区（用于发送语音前的静音块）
    this._chunkBuffer = [];
    this._maxBufferedChunks = Math.ceil(this.speechPadMs / 100); // 100ms per chunk
    this._isFirstChunk = true;
    this._headerBuffer = [];       // 缓冲初始 WebM header 碎片（Android 兼容）
    this._headerBufferSize = 0;
    this._headerFlushed = false;
    this._speechEndTimer = null;
    this._isSendingTail = false;

    // VAD 就绪标志：初始化完成前所有块直接发送，避免 Google STT 超时
    this._vadReady = false;

    // 静默心跳：防止后端 audio_generator 超时
    this._lastSendTime = 0;
    this._keepAliveIntervalMs = 3000; // 每 3 秒发一个静音块保活（Google STT 要求近实时）

    // RNNoise VAD 概率（从 AudioWorklet 接收）
    this.rnnoiseVadProb = 0;

    // 诊断计数器（暴露给 UI 用于排查）
    this.stats = {
      chunksProduced: 0,  // MediaRecorder 产出的 chunk 数
      chunksSent: 0,      // 通过 onAudioData 发送的 chunk 数
      chunksDropped: 0,   // VAD 静音期间丢弃的 chunk 数
    };
  }

  /**
   * 启动管线
   * @param {MediaStream} micStream - 麦克风 MediaStream
   * @returns {Promise<MediaStream>} 降噪后的 MediaStream（用于外部需要）
   */
  async start(micStream) {
    this._micStream = micStream;
    this.isRunning = true;
    this._fullAudioChunks = [];
    this._chunkBuffer = [];
    this._isFirstChunk = true;
    this._headerBuffer = [];
    this._headerBufferSize = 0;
    this._headerFlushed = false;
    this._lastSendTime = Date.now();
    this.isSpeaking = false;

    try {
      // 1. 创建 AudioContext
      // 移动端可能不支持指定采样率，优先使用设备原生采样率
      let audioCtx;
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });
      } catch (_) {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      }
      this._audioContext = audioCtx;

      // 移动端 AudioContext 默认 suspended，必须显式 resume
      if (this._audioContext.state === 'suspended') {
        await this._audioContext.resume();
      }
      console.log('[AudioPipeline] AudioContext state:', this._audioContext.state,
        'sampleRate:', this._audioContext.sampleRate);

      // 2. 创建源节点
      this._sourceNode = this._audioContext.createMediaStreamSource(micStream);

      let outputNode = this._sourceNode;

      // 3. 加载 RNNoise AudioWorklet（如果启用降噪）
      if (this.enableDenoise) {
        try {
          await this._audioContext.audioWorklet.addModule('/audio-worklet/denoise-processor.js');

          this._denoiseNode = new AudioWorkletNode(this._audioContext, 'denoise-processor');

          // 监听来自 AudioWorklet 的消息
          this._denoiseNode.port.onmessage = (e) => {
            const msg = e.data;
            if (msg.type === 'ready') {
              console.log('[AudioPipeline] RNNoise ready');
            } else if (msg.type === 'vad') {
              this.rnnoiseVadProb = msg.probability;
            } else if (msg.type === 'error') {
              console.error('[AudioPipeline] RNNoise error:', msg.message);
              this.onError('RNNoise 初始化失败: ' + msg.message);
            }
          };

          this._sourceNode.connect(this._denoiseNode);
          outputNode = this._denoiseNode;
          console.log('[AudioPipeline] RNNoise AudioWorklet loaded');
        } catch (e) {
          console.warn('[AudioPipeline] AudioWorklet load failed, falling back to passthrough:', e);
          // 降级：不降噪，直接使用原始音频
          this.enableDenoise = false;
        }
      }

      // 4. 创建 MediaStreamDestination（降噪后的流）
      this._bridgeGain = this._audioContext.createGain();
      this._bridgeGain.gain.value = 1;
      this._destinationNode = this._audioContext.createMediaStreamDestination();
      outputNode.connect(this._bridgeGain);
      this._bridgeGain.connect(this._destinationNode);

      const denoisedStream = this._destinationNode.stream;

      // 5. 选择兼容的 MIME type
      const mimeType = this._pickMimeType();
      console.log('[AudioPipeline] MediaRecorder mimeType:', mimeType);

      // 6. 创建 MediaRecorder（使用降噪后的流）
      this._mediaRecorder = new MediaRecorder(denoisedStream,
        mimeType ? { mimeType } : undefined
      );

      this._mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0 && this.isRunning) {
          this.stats.chunksProduced++;
          this._handleAudioChunk(event.data);
        }
      };

      this._mediaRecorder.onerror = (e) => {
        console.error('[AudioPipeline] MediaRecorder error:', e);
      };

      this._mediaRecorder.start(100);

      // 7. 创建完整录音 Recorder（降噪后的流）
      this._fullRecorder = new MediaRecorder(denoisedStream,
        mimeType ? { mimeType } : undefined
      );
      this._fullRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this._fullAudioChunks.push(event.data);
        }
      };
      this._fullRecorder.start();

      // 7. 初始化 Silero VAD（如果启用）
      if (this.enableVad) {
        await this._initVad(denoisedStream);
      } else {
        // 不启用 VAD 时，所有数据都发送
        this.isSpeaking = true;
        this.onVadChange(true);
      }

      this.onReady();
      console.log('[AudioPipeline] Pipeline started',
        `denoise=${this.enableDenoise}`,
        `vad=${this.enableVad}`
      );

      return denoisedStream;
    } catch (e) {
      this.onError('管线启动失败: ' + e.message);
      throw e;
    }
  }

  /**
   * 初始化 Silero VAD
   */
  async _initVad(denoisedStream) {
    try {
      const VadClass = await loadVadModule();
      if (!VadClass) {
        console.warn('[AudioPipeline] VAD module not available, disabling VAD gating');
        this.enableVad = false;
        this.isSpeaking = true;
        this.onVadChange(true);
        return;
      }

      this._micVad = await VadClass.new({
        // 自定义音频流：使用降噪后的流
        stream: denoisedStream,

        // 模型和运行时路径（通过脚本注入时 ort 已全局配置）
        model: 'v5',
        baseAssetPath: '/vad/',
        onnxWASMBasePath: '/vad/',

        // VAD 参数
        positiveSpeechThreshold: this.vadThreshold,
        negativeSpeechThreshold: this.vadThreshold - 0.15,
        minSpeechFrames: 3,
        preSpeechPadFrames: 3,
        startOnLoad: false,

        // 覆盖默认的 getStream：返回降噪后的流（不要重新获取麦克风）
        getStream: async () => denoisedStream,
        pauseStream: async () => { /* 不暂停，由 pipeline.stop() 统一管理 */ },
        resumeStream: async () => denoisedStream,

        // 回调
        onSpeechStart: () => {
          console.log('[AudioPipeline] VAD: speech start');
          this._onSpeechStart();
        },

        onSpeechEnd: () => {
          console.log('[AudioPipeline] VAD: speech end');
          this._onSpeechEnd();
        },

        onFrameProcessed: (probabilities) => {
          // 可选：实时 frame 级别的概率
        }
      });

      this._micVad.start();
      this._vadReady = true;
      console.log('[AudioPipeline] Silero VAD initialized, gating enabled');
    } catch (e) {
      console.warn('[AudioPipeline] Silero VAD init failed:', e);
      this.enableVad = false;
      this.isSpeaking = true;
      this.onVadChange(true);
    }
  }

  /**
   * 语音开始
   */
  _onSpeechStart() {
    if (this._speechEndTimer) {
      clearTimeout(this._speechEndTimer);
      this._speechEndTimer = null;
    }

    if (!this.isSpeaking) {
      this.isSpeaking = true;
      this._isSendingTail = false;
      this.onVadChange(true);

      // 发送缓冲区中的最近音频块（语音前的填充）
      for (const chunk of this._chunkBuffer) {
        this.stats.chunksSent++;
        this.onAudioData(chunk);
      }
      this._chunkBuffer = [];
    }
  }

  /**
   * 语音结束
   */
  _onSpeechEnd() {
    // 延迟结束，添加尾部填充
    if (this._speechEndTimer) {
      clearTimeout(this._speechEndTimer);
    }

    this._isSendingTail = true;

    this._speechEndTimer = setTimeout(() => {
      this.isSpeaking = false;
      this._isSendingTail = false;
      this.onVadChange(false);
      this._speechEndTimer = null;
    }, this.speechPadMs);
  }

  /**
   * 处理 MediaRecorder 产生的音频块
   */
  _handleAudioChunk(blob) {
    const send = (b) => {
      this.stats.chunksSent++;
      this.onAudioData(b);
    };

    // Android Chrome 的 MediaRecorder 从 MediaStreamDestination 录制时，
    // 会将 WebM header 碎片化为多个极小块（首块仅 1 字节）。
    // 缓冲前 1 秒的块，合并为一个完整的 header blob 再发送。
    if (!this._headerFlushed) {
      this._headerBuffer.push(blob);
      this._headerBufferSize += blob.size;

      // 当累积超过 2KB 或已缓冲 10 个块（~1秒），视为 header 完整
      if (this._headerBufferSize >= 2048 || this._headerBuffer.length >= 10) {
        const combined = new Blob(this._headerBuffer, { type: blob.type });
        this._headerFlushed = true;
        this._headerBuffer = null; // 释放
        console.log('[AudioPipeline] Header flushed:',
          combined.size, 'bytes from', this._headerBufferSize, 'bytes in',
          (this._headerFlushed ? 'combined' : 'single'));
        send(combined);
      }
      return;
    }

    if (!this.enableVad || !this._vadReady) {
      // VAD 未启用或尚未初始化完成，全部发送（避免 Google STT 超时）
      send(blob);
      return;
    }

    if (this.isSpeaking || this._isSendingTail) {
      // 正在说话或尾部填充期间，直接发送
      send(blob);
      this._lastSendTime = Date.now();
    } else {
      // 静音期间：定期发送一个块作为心跳，防止后端 30 秒超时
      const now = Date.now();
      if (now - this._lastSendTime >= this._keepAliveIntervalMs) {
        send(blob);
        this._lastSendTime = now;
      } else {
        this.stats.chunksDropped++;
      }

      // 缓冲最近的块（用于下次语音开始时的前填充）
      this._chunkBuffer.push(blob);
      if (this._chunkBuffer.length > this._maxBufferedChunks) {
        this._chunkBuffer.shift();
      }
    }
  }

  /**
   * 停止管线
   * @returns {Promise<{audioBlob: Blob, audioChunks: Blob[]}>}
   */
  async stop() {
    this.isRunning = false;

    // 清理定时器
    if (this._speechEndTimer) {
      clearTimeout(this._speechEndTimer);
      this._speechEndTimer = null;
    }

    // 停止 Silero VAD
    if (this._micVad) {
      try {
        this._micVad.pause();
        this._micVad.destroy();
      } catch (e) {
        console.warn('[AudioPipeline] VAD cleanup error:', e);
      }
      this._micVad = null;
    }

    // 停止流式 MediaRecorder
    if (this._mediaRecorder && this._mediaRecorder.state !== 'inactive') {
      this._mediaRecorder.stop();
    }
    this._mediaRecorder = null;

    // 停止完整录音
    if (this._fullRecorder && this._fullRecorder.state !== 'inactive') {
      this._fullRecorder.stop();
    }

    // 等待最后的数据
    await new Promise(resolve => setTimeout(resolve, 200));

    // 断开音频节点
    if (this._denoiseNode) {
      this._denoiseNode.disconnect();
      this._denoiseNode = null;
    }
    if (this._bridgeGain) {
      this._bridgeGain.disconnect();
      this._bridgeGain = null;
    }
    if (this._sourceNode) {
      this._sourceNode.disconnect();
      this._sourceNode = null;
    }
    if (this._destinationNode) {
      this._destinationNode = null;
    }

    // 关闭 AudioContext
    if (this._audioContext) {
      await this._audioContext.close();
      this._audioContext = null;
    }

    // 停止麦克风
    if (this._micStream) {
      this._micStream.getTracks().forEach(t => t.stop());
      this._micStream = null;
    }

    this._fullRecorder = null;

    // 构建完整录音
    const audioBlob = this._fullAudioChunks.length > 0
      ? new Blob(this._fullAudioChunks, { type: 'audio/webm' })
      : null;

    return {
      audioBlob,
      audioChunks: this._fullAudioChunks
    };
  }

  /**
   * 获取完整录音数据块
   */
  getFullAudioChunks() {
    return this._fullAudioChunks;
  }

  /**
   * 选择浏览器支持的 MediaRecorder MIME type
   * @returns {string|null}
   */
  _pickMimeType() {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    for (const mime of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) {
        return mime;
      }
    }
    // 返回 null 让浏览器选择默认类型
    console.warn('[AudioPipeline] No preferred MIME type supported, using browser default');
    return null;
  }
}

/**
 * 预加载所有音频管线依赖（RNNoise WASM + Silero VAD ONNX）。
 * 调用后资源会进入浏览器缓存，录音时可秒级加载。
 *
 * @param {function({stage: string, loaded: number, total: number}): void} [onProgress]
 * @returns {Promise<{denoise: boolean, vad: boolean}>} 各模块是否加载成功
 */
AudioPipeline.preload = async function preload(onProgress) {
  const progress = onProgress || (() => {});
  const result = { denoise: false, vad: false };

  const DENOISE_ASSETS = [
    '/audio-worklet/denoise-processor.js',
    '/audio-worklet/rnnoise-sync.js',
    '/audio-worklet/rnnoise.wasm',
  ];

  const VAD_ASSETS = [
    '/vad/ort.min.js',
    '/vad/bundle.min.js',
    '/vad/silero_vad_v5.onnx',
    '/vad/ort-wasm-simd-threaded.wasm',
    '/vad/ort-wasm-simd-threaded.mjs',
    '/vad/vad.worklet.bundle.min.js',
  ];

  const total = DENOISE_ASSETS.length + VAD_ASSETS.length;
  let loaded = 0;

  const fetchOne = async (url) => {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`${url}: ${resp.status}`);
    await resp.arrayBuffer();
    loaded++;
    progress({ stage: 'downloading', loaded, total });
  };

  // 阶段 1：RNNoise
  try {
    progress({ stage: 'denoise', loaded, total });
    await Promise.all(DENOISE_ASSETS.map(fetchOne));
    result.denoise = true;
    console.log('[AudioPipeline] Denoise assets preloaded');
  } catch (e) {
    console.warn('[AudioPipeline] Denoise preload failed:', e);
  }

  // 阶段 2：VAD（包括执行 script 标签加载 ort + bundle）
  try {
    progress({ stage: 'vad', loaded, total });
    await Promise.all(VAD_ASSETS.map(fetchOne));
    await loadVadModule();
    result.vad = true;
    console.log('[AudioPipeline] VAD assets preloaded');
  } catch (e) {
    console.warn('[AudioPipeline] VAD preload failed:', e);
  }

  return result;
};
